"use client";

import { setNavVisible } from "@/store/navVisibilitySlice";
import { AppState } from "@/store/store";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * This component is used to show/hide the navigation bars when the user scrolls down and show it when the user scrolls back up.
 * It sets a CSS custom property that can be used by other components.
 * This allows navigation components to remain server components.
 */
export default function ScrollHideController() {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const isDarkened = useSelector((state: AppState) => state.overlayDarkeningSlice.isDarkened);
  const dispatch = useDispatch();

  useScrollPosition(
    ({ currPos }) => {
      const currentScrollY = Math.abs(currPos.y);

      // Ignore scroll events when darkened overlay (drawer menu, etc.)
      // Setting overflow:hidden can cause tiny scroll position changes due to scrollbar disappearing
      if (isDarkened) {
        return;
      }

      if (currentScrollY > lastScrollY && currentScrollY > 150) {
        // Scrolling down and past 150px - hide navigation
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show navigation
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    },
    [lastScrollY, isDarkened],
  );

  useEffect(() => {
    // Set CSS custom property on document root
    // The navigation bars will rely on this CSS custom property to determine if they should be visible or not.
    document.documentElement.style.setProperty("--nav-visible", isVisible ? "1" : "0");
    // CSS var (above) feeds CSS-only consumers; Redux mirror feeds JS consumers (PDP pinned bar).
    dispatch(setNavVisible(isVisible));
  }, [isVisible, dispatch]);

  // This component renders nothing - it just manages the CSS custom property
  return null;
}
